import React, { useCallback } from 'react';
import { Accordion, Button, TextField, Switch, Tag } from '@scale/scaleui';
import { GlobalWorkerSkillScorePermission } from '@scale/shared/taskerScreening/types';
import { QualificationType } from '@scale/shared/types/qualification/types';
import SelectQualification from 'react-scale/corp/components/SelectQualification';
import { defaultWorkerSkillDimension, WorkerSkillDimension } from '@scale/shared-types';

interface GlobalWorkerSkillScorePermissionsBuilderProps {
  globalScoreFilters: GlobalWorkerSkillScorePermission[];
  setGlobalScoreFilters: (filters: GlobalWorkerSkillScorePermission[]) => void;
}

const GlobalWorkerSkillScorePermissionsBuilder: React.FC<
  GlobalWorkerSkillScorePermissionsBuilderProps
> = ({ globalScoreFilters, setGlobalScoreFilters }) => {
  const addGlobalScoreFilter = () => {
    const newFilter = {
      workerSkillId: '',
      workerSkillDimension: defaultWorkerSkillDimension,
      threshold: 500,
      allowNonExistingScores: true,
    };
    setGlobalScoreFilters([...globalScoreFilters, newFilter]);
  };

  const deleteGlobalScoreFilter = (index: number) => () => {
    const newFilters = [...globalScoreFilters];
    newFilters.splice(index, 1);
    setGlobalScoreFilters(newFilters);
  };

  const handleUpdateWorkerSkill = useCallback(
    (globalScoreFilterIndex: number) => {
      return (value: any) => {
        const newFilters = [...globalScoreFilters];
        if (Object.values(WorkerSkillDimension).includes(value)) {
          newFilters[globalScoreFilterIndex].workerSkillDimension = value;
        } else {
          newFilters[globalScoreFilterIndex].workerSkillId = value?.value ?? '';
        }
        setGlobalScoreFilters(newFilters);
      };
    },
    [globalScoreFilters, setGlobalScoreFilters],
  );

  return (
    <div>
      {globalScoreFilters.map((filter, i) => (
        <Accordion
          key={`${i}-${globalScoreFilters.length}`}
          className="mt-4"
          header={
            <div className="flex flex-row w-full align-center">
              <div className="mt-1">Global Score Filter {i + 1}</div>
              {filter.workerSkillId?.length ? (
                <Tag className="ml-4" variant="primary">
                  {filter.workerSkillId} - {filter.threshold}
                </Tag>
              ) : (
                <Tag className="ml-4" variant="danger">
                  No Skill Selected
                </Tag>
              )}
              <Button
                className="ml-auto mr-4"
                variant="danger"
                onClick={deleteGlobalScoreFilter(i)}
              >
                Delete
              </Button>
            </div>
          }
          target={
            <div>
              <SelectQualification
                value={
                  filter.workerSkillId?.length
                    ? {
                        qualificationType: QualificationType.WorkerSkill,
                        workerSkill: {
                          dimension: filter.workerSkillDimension,
                          label: filter.workerSkillId,
                          value: filter.workerSkillId,
                        },
                      }
                    : undefined
                }
                onWorkerSkillChange={handleUpdateWorkerSkill(i)}
                qualificationType={QualificationType.WorkerSkill}
                requireStatus={false}
              />
              <div className="mt-4">Worker Skill Threshold (0 - 1000)</div>
              <TextField
                id="threshold"
                className="mt-2"
                value={filter.threshold}
                onChange={e => {
                  const newFilters = [...globalScoreFilters];
                  newFilters[i].threshold = Number(e.target.value);
                  setGlobalScoreFilters(newFilters);
                }}
                type="number"
              />
              <Switch
                className="mt-4 mb-2"
                label="Allow non-existing scores even if threshold value is set"
                altDescription="Allow non-existing scores even if threshold value is set"
                checked={filter.allowNonExistingScores}
                onCheckedChange={checked => {
                  const newFilters = [...globalScoreFilters];
                  newFilters[i].allowNonExistingScores = checked;
                  setGlobalScoreFilters(newFilters);
                }}
              />
            </div>
          }
        />
      ))}
      <Button className="mt-4" variant="secondary" onClick={addGlobalScoreFilter}>
        Add Global Score Filter
      </Button>
    </div>
  );
};

export default GlobalWorkerSkillScorePermissionsBuilder;
